import type { Breadcrumb } from '@sentry/nextjs';

/**
 * BAL-1144 R4: no row value may leave in a Sentry event.
 *
 * Sentry records every outgoing request (and every console line) as a
 * breadcrumb and attaches the last ones to each event. For a Supabase call
 * that breadcrumb carries the PostgREST query, so `email=eq.anna@x.com`
 * reached Sentry on every event that followed it. Measured 2026-10-08 on
 * production issue BALLEE-MK: `http.query` was
 * `?select=zz_db_failure_probe&limit=1`.
 *
 * The query SHAPE is what debugging needs (which table, columns and filter
 * operator), so it is kept. Every filter VALUE is replaced:
 *
 *   ?select=id,email&email=eq.anna@x.com&id=in.(1,2)&limit=1
 *   ?select=id,email&email=eq.[Filtered]&id=in.[Filtered]&limit=1
 *
 * The same rule applies to every host: a third-party query value can carry
 * personal data just as well. Anything that does not match a known safe shape
 * is filtered, never kept.
 */

export const FILTERED = '[Filtered]';

/**
 * Parameters whose value is query structure, never row data, each with the
 * only syntax accepted for it. A value outside that syntax is filtered: a
 * key name alone proves nothing (`order=eq.anna@x.com`).
 */
const ORDER_TERM =
  '[A-Za-z0-9_]+(?:->>?[A-Za-z0-9_]+)*(?:\\.(?:asc|desc|nullsfirst|nullslast))*';

// A Map, not an object literal: `toString` or `__proto__` as a query key must
// not resolve to an inherited property.
const STRUCTURAL_PARAMS = new Map<string, RegExp>([
  // columns, embeds, aliases, casts, json paths: `*,rel!inner(id),a:b::text`
  ['select', /^[A-Za-z0-9_*,.:!()\->]*$/],
  ['columns', /^[A-Za-z0-9_,"]*$/],
  ['order', new RegExp(`^${ORDER_TERM}(?:,${ORDER_TERM})*$`)],
  ['on_conflict', /^[A-Za-z0-9_,]*$/],
  ['limit', /^\d+$/],
  ['offset', /^\d+$/],
]);

/** A key kept verbatim. Anything else (an email used as a key) is filtered. */
const SAFE_KEY = /^[A-Za-z0-9_.\-[\]]{1,64}$/;

/**
 * A PostgREST operator prefix, optionally negated, with an optional modifier:
 * `eq.`, `not.ilike.`, `fts(english).`, `like(any).`. Matched on the DECODED
 * value, and emitted decoded, so the event says HOW a column was filtered.
 */
const POSTGREST_OPERATOR =
  /^(?:not\.)?(?:eq|neq|gt|gte|lt|lte|like|ilike|match|imatch|is|isdistinct|in|cs|cd|sl|sr|nxr|nxl|adj|ov|fts|plfts|phfts|wfts)(?:\([A-Za-z_]+\))?\./;

function safeDecode(text: string): string {
  try {
    return decodeURIComponent(text.replace(/\+/g, ' '));
  } catch {
    return text;
  }
}

function redactParam(pair: string): string {
  if (pair === '') return pair;
  const eq = pair.indexOf('=');
  const key = eq === -1 ? pair : pair.slice(0, eq);
  const decodedKey = safeDecode(key);
  const safeKey = SAFE_KEY.test(decodedKey) ? key : FILTERED;
  // A bare token (`?abc123`) is a value without a key.
  if (eq === -1) return FILTERED;
  const value = pair.slice(eq + 1);
  if (value === '') return `${safeKey}=`;
  const decoded = safeDecode(value);
  const op = POSTGREST_OPERATOR.exec(decoded);
  // A value shaped like a filter (`order=eq.x`) is a filter, whatever its key.
  const syntax = STRUCTURAL_PARAMS.get(decodedKey);
  if (!op && syntax && safeKey === key && syntax.test(decoded)) return pair;
  return `${safeKey}=${op ? op[0] : ''}${FILTERED}`;
}

/** Redact a query string, with or without its leading `?`. */
export function redactQueryValues(query: string): string {
  const hasMark = query.startsWith('?');
  const body = hasMark ? query.slice(1) : query;
  if (body === '') return query;
  const redacted = body.split('&').map(redactParam).join('&');
  return hasMark ? `?${redacted}` : redacted;
}

/** Redact the query of a URL and drop its fragment, which can carry tokens. */
export function redactUrl(url: string): string {
  const hashAt = url.indexOf('#');
  const noFragment = hashAt === -1 ? url : url.slice(0, hashAt);
  const queryAt = noFragment.indexOf('?');
  if (queryAt === -1) return noFragment;
  return (
    noFragment.slice(0, queryAt) + redactQueryValues(noFragment.slice(queryAt))
  );
}

/**
 * Every `?query` (and `#fragment`) inside free text, such as a console line
 * `GET https://x/rest/v1/profiles?email=eq.a@b.c`. A `?` with no `key=value`
 * after it (`what?`) is left as it was.
 */
// Stops at whitespace, `"`, `<`, `>` and backtick only: an apostrophe is legal
// inside a query value (`name=eq.O'Connor`).
const TEXT_QUERY = /\?([^\s"<>`#]*)(#[^\s"<>`]*)?/g;

export function redactText(text: string): string {
  if (!text.includes('?') && !text.includes('#')) return text;
  return text.replace(TEXT_QUERY, (whole, query: string, fragment?: string) => {
    if (!query.includes('=') && !fragment) return whole;
    return redactQueryValues(`?${query}`);
  });
}

const URL_FIELDS = ['url', 'from', 'to', 'http.url', 'url.full'] as const;

/**
 * `beforeBreadcrumb` for every runtime. Returns a redacted copy and never
 * mutates the breadcrumb it was given:
 * - `message` has its query values filtered (a console line carries its URL
 *   there);
 * - `data.arguments` is dropped. Sentry already joins every console argument
 *   into `message`, and the arguments are the application's live objects:
 *   walking them was the cost, mutation and fidelity risk review pass 2 found;
 * - the URL fields lose their query values and fragment, `http.query` its
 *   values, and `http.fragment` is removed.
 * Never drops a breadcrumb.
 */
export function redactBreadcrumbQuery(breadcrumb: Breadcrumb): Breadcrumb {
  const out: Breadcrumb = { ...breadcrumb };
  if (typeof breadcrumb.message === 'string') {
    out.message = redactText(breadcrumb.message);
  }
  const data = breadcrumb.data;
  if (!data || typeof data !== 'object') return out;

  const copy: Record<string, unknown> = { ...data };
  delete copy.arguments;
  delete copy['http.fragment'];
  for (const field of URL_FIELDS) {
    const value = copy[field];
    if (typeof value === 'string') copy[field] = redactUrl(value);
  }
  const query = copy['http.query'];
  if (typeof query === 'string') copy['http.query'] = redactQueryValues(query);
  out.data = copy;
  return out;
}
